import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, UserCog, Ban, CheckCircle, Trash2, ChevronLeft, ChevronRight, MoreHorizontal, Mail, KeyRound } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Constants } from "@/integrations/supabase/types";
import { SetTemporaryPasswordDialog } from "./SetTemporaryPasswordDialog";

const allRoles = Constants.public.Enums.app_role;
const PER_PAGE = 10;

const roleLabels: Record<string, string> = {
  superadmin: "Super Admin",
  principal: "Principal",
  deputy_principal: "Deputy Principal",
  head_admission: "Head of Admission",
  head_administration: "Head of Administration",
  head_academic: "Head of Academic",
  head_examination: "Head of Examination",
  head_accounting: "Head of Accounting",
  head_student_service: "Head of Student Services",
  head_discipline: "Head of Discipline",
  hod_cmt: "HoD CMT",
  hod_pst: "HoD PST",
  hod_sw: "HoD SW",
  it_incharge: "IT Incharge",
  security_man: "Security Man",
  cr_cmt4: "CR CMT4",
  cr_cmt5: "CR CMT5",
  cr_cmt6: "CR CMT6",
  cr_pst4: "CR PST4",
  cr_pst5: "CR PST5",
  cr_pst6: "CR PST6",
  cr_sw4: "CR SW4",
  cr_sw5: "CR SW5",
  cr_sw6: "CR SW6",
  auditor: "Auditor",
  student: "Student",
};

const roleBadgeColors: Record<string, string> = {
  superadmin: "bg-red-100 text-red-800 border-red-200",
  principal: "bg-blue-100 text-blue-800 border-blue-200",
  deputy_principal: "bg-indigo-100 text-indigo-800 border-indigo-200",
  head_admission: "bg-emerald-100 text-emerald-800 border-emerald-200",
  head_administration: "bg-teal-100 text-teal-800 border-teal-200",
  head_academic: "bg-purple-100 text-purple-800 border-purple-200",
  head_examination: "bg-amber-100 text-amber-800 border-amber-200",
  head_accounting: "bg-orange-100 text-orange-800 border-orange-200",
  head_student_service: "bg-cyan-100 text-cyan-800 border-cyan-200",
  head_discipline: "bg-rose-100 text-rose-800 border-rose-200",
  hod_cmt: "bg-violet-100 text-violet-800 border-violet-200",
  hod_pst: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
  hod_sw: "bg-pink-100 text-pink-800 border-pink-200",
  it_incharge: "bg-slate-100 text-slate-800 border-slate-200",
  security_man: "bg-stone-100 text-stone-800 border-stone-200",
  cr_cmt4: "bg-lime-100 text-lime-800 border-lime-200",
  cr_cmt5: "bg-lime-100 text-lime-800 border-lime-200",
  cr_cmt6: "bg-lime-100 text-lime-800 border-lime-200",
  cr_pst4: "bg-green-100 text-green-800 border-green-200",
  cr_pst5: "bg-green-100 text-green-800 border-green-200",
  cr_pst6: "bg-green-100 text-green-800 border-green-200",
  cr_sw4: "bg-yellow-100 text-yellow-800 border-yellow-200",
  cr_sw5: "bg-yellow-100 text-yellow-800 border-yellow-200",
  cr_sw6: "bg-yellow-100 text-yellow-800 border-yellow-200",
  auditor: "bg-zinc-100 text-zinc-800 border-zinc-200",
  student: "bg-sky-100 text-sky-800 border-sky-200",
};

const FILTER_OPTIONS = [
  { value: "all", label: "All Roles" },
  { value: "superadmin", label: "Super Admin" },
  { value: "principal", label: "Principal" },
  { value: "deputy_principal", label: "Deputy Principal" },
  { value: "head_admission", label: "Head of Admission" },
  { value: "head_administration", label: "Head of Administration" },
  { value: "head_academic", label: "Head of Academic" },
  { value: "head_examination", label: "Head of Examination" },
  { value: "head_accounting", label: "Head of Accounting" },
  { value: "head_student_service", label: "Head of Student Services" },
  { value: "head_discipline", label: "Head of Discipline" },
  { value: "hods", label: "HODs" },
  { value: "it_incharge", label: "IT Incharge" },
  { value: "security_man", label: "Security Man" },
  { value: "cr_cmt4", label: "CR CMT4" },
  { value: "cr_cmt5", label: "CR CMT5" },
  { value: "cr_cmt6", label: "CR CMT6" },
  { value: "cr_pst4", label: "CR PST4" },
  { value: "cr_pst5", label: "CR PST5" },
  { value: "cr_pst6", label: "CR PST6" },
  { value: "cr_sw4", label: "CR SW4" },
  { value: "cr_sw5", label: "CR SW5" },
  { value: "cr_sw6", label: "CR SW6" },
  { value: "auditor", label: "Auditor" },
  { value: "student", label: "Student" },
];

const HOD_ROLES = ["hod_cmt", "hod_pst", "hod_sw"];

interface PermRow {
  role: string;
  module_key: string;
  can_create: boolean;
  can_read: boolean;
  can_update: boolean;
  can_delete: boolean;
}

export function AllUsersTab() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newRole, setNewRole] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [tempPwUser, setTempPwUser] = useState<any>(null);
  const [sendingResetFor, setSendingResetFor] = useState<string | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users-with-roles"],
    queryFn: async () => {
      const { data: profiles, error } = await supabase.from("profiles").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      const { data: roles } = await supabase.from("user_roles").select("*");
      return (profiles ?? []).map(p => ({
        ...p,
        roles: (roles ?? []).filter(r => r.user_id === p.user_id).map(r => r.role),
      }));
    },
  });

  const { data: permissions = [] } = useQuery({
    queryKey: ["role-permissions"],
    queryFn: async () => {
      const { data, error } = await supabase.from("role_permissions").select("role, module_key, can_create, can_read, can_update, can_delete");
      if (error) throw error;
      return data as PermRow[];
    },
  });

  // Filter
  const filtered = users.filter(u => {
    const matchesSearch = `${u.full_name || ""} ${(u as any).email || ""}`.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (roleFilter === "all") return true;
    if (roleFilter === "hods") return u.roles.some((r: string) => HOD_ROLES.includes(r));
    return u.roles.includes(roleFilter as any);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  // Get aggregated CRUD for a user across all their roles
  const getUserPerms = (userRoles: string[]) => {
    let c = false, r = false, u = false, d = false;
    for (const role of userRoles) {
      const rolePerms = permissions.filter(p => p.role === role);
      for (const p of rolePerms) {
        if (p.can_create) c = true;
        if (p.can_read) r = true;
        if (p.can_update) u = true;
        if (p.can_delete) d = true;
      }
    }
    return { c, r, u, d };
  };

  const addRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase.from("user_roles").insert({ user_id: userId, role: role as any });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] });
      toast({ title: "Role assigned" });
      setRoleDialogOpen(false);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const removeRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", role as any) as any;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] });
      toast({ title: "Role removed" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const suspendMutation = useMutation({
    mutationFn: async ({ userId, suspend }: { userId: string; suspend: boolean }) => {
      const { error } = await supabase.from("profiles").update({ status: suspend ? "suspended" : "active" }).eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: (_, { suspend }) => {
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] });
      toast({ title: suspend ? "User suspended" : "User reactivated" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/delete-user`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ user_id: userId }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to delete user");
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] });
      toast({ title: "User deleted permanently" });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const isSelf = (u: any) => u.user_id === currentUser?.id;

  const sendResetEmail = async (u: any) => {
    setSendingResetFor(u.user_id);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/admin-send-password-reset`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ user_id: u.user_id }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to send reset email");
      toast({ title: "Reset link sent", description: `Email sent to ${result.email}` });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSendingResetFor(null);
    }
  };

  return (
    <>
      {/* Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manage Roles — {selectedUser?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Current Roles</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedUser?.roles.length === 0 && <p className="text-sm text-muted-foreground">No roles assigned</p>}
                {selectedUser?.roles.map((r: string) => (
                  <Badge key={r} variant="outline" className={`flex items-center gap-1 ${roleBadgeColors[r] || ""}`}>
                    {roleLabels[r] || r}
                    <button onClick={() => removeRoleMutation.mutate({ userId: selectedUser.user_id, role: r })} className="ml-1 text-destructive hover:text-destructive/80">×</button>
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>Add Role</Label>
              <div className="flex gap-2 mt-1">
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    {allRoles.filter(r => !selectedUser?.roles.includes(r)).map(r => (
                      <SelectItem key={r} value={r}>{roleLabels[r] || r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button disabled={!newRole || addRoleMutation.isPending} onClick={() => newRole && addRoleMutation.mutate({ userId: selectedUser.user_id, role: newRole })}>
                  Add
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SetTemporaryPasswordDialog
        open={!!tempPwUser}
        onOpenChange={(o) => { if (!o) setTempPwUser(null); }}
        userId={tempPwUser?.user_id ?? null}
        userName={tempPwUser?.full_name ?? null}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{userToDelete?.full_name}</strong>'s account. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={() => userToDelete && deleteMutation.mutate(userToDelete.user_id)}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name or email..." className="pl-9" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTER_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg">All Users</CardTitle>
          <CardDescription>{filtered.length} users{search && ` matching "${search}"`}{roleFilter !== "all" && ` · ${FILTER_OPTIONS.find(o => o.value === roleFilter)?.label}`}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative w-full overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden sm:table-cell">Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="hidden md:table-cell">Permissions</TableHead>
                  <TableHead className="hidden lg:table-cell">Joined</TableHead>
                  <TableHead className="w-10">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map((u: any) => {
                  const perms = getUserPerms(u.roles);
                  return (
                    <TableRow key={u.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{u.full_name || "Unnamed"}</p>
                          <p className="text-xs text-muted-foreground sm:hidden">{u.email || "—"}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{u.email || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {u.roles.length === 0 && <span className="text-xs text-muted-foreground">No roles</span>}
                          {u.roles.map((r: string) => (
                            <Badge key={r} variant="outline" className={`text-[10px] px-1.5 py-0 ${roleBadgeColors[r] || ""}`}>
                              {roleLabels[r] || r}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {perms.c && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-green-50 text-green-700 border-green-200">Create</Badge>}
                          {perms.r && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">Read</Badge>}
                          {perms.u && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">Update</Badge>}
                          {perms.d && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-red-50 text-red-700 border-red-200">Delete</Badge>}
                          {!perms.c && !perms.r && !perms.u && !perms.d && <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setSelectedUser(u); setNewRole(""); setRoleDialogOpen(true); }}>
                              <UserCog className="mr-2 h-4 w-4" />Manage Roles
                            </DropdownMenuItem>
                            {!isSelf(u) && (
                              <>
                                <DropdownMenuItem
                                  disabled={sendingResetFor === u.user_id}
                                  onClick={() => sendResetEmail(u)}
                                >
                                  <Mail className="mr-2 h-4 w-4" />
                                  {sendingResetFor === u.user_id ? "Sending..." : "Send Reset Link"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setTempPwUser(u)}>
                                  <KeyRound className="mr-2 h-4 w-4" />Set Temporary Password
                                </DropdownMenuItem>
                                {u.status === "suspended" ? (
                                  <DropdownMenuItem onClick={() => suspendMutation.mutate({ userId: u.user_id, suspend: false })}>
                                    <CheckCircle className="mr-2 h-4 w-4" />Activate
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => suspendMutation.mutate({ userId: u.user_id, suspend: true })}>
                                    <Ban className="mr-2 h-4 w-4" />Suspend
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => { setUserToDelete(u); setDeleteDialogOpen(true); }}>
                                  <Trash2 className="mr-2 h-4 w-4" />Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      {isLoading ? "Loading..." : "No users found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4 pt-4 border-t">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Showing {(currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, filtered.length)} of {filtered.length}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs sm:text-sm text-muted-foreground">Page {currentPage}/{totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
